import { useQueryJoinedOrganizations } from '@/hooks/query/useQueryJoinedOrganization';
import { useQueryJoinedTeams } from '@/hooks/query/useQueryJoinedTeams';
import { useQueryTeamDetails } from '@/hooks/query/useQueryTeamDetails';

export interface SessionSharingPolicy {
  isDirectSharingAllowed: boolean;
  isLinkSharingAllowed: boolean;
}

/**
 * Effective session sharing policy for the current user.
 *
 * Organization policy takes precedence; otherwise the standalone team's policy
 * applies. Team details already report the organization's values for a team
 * inside an organization (`session_sharing_locked_by_org`), so the team branch
 * needs no `parent_id` check. Direct sharing is allowed by default while the
 * applicable policy is unavailable, which mirrors the BE resolver. Link sharing
 * stays off until the organization query settles, because an organization's
 * link sharing defaults to off.
 */
export function useSessionSharingPolicy(): SessionSharingPolicy {
  const { data: joinedOrgData, isPending: isOrgPending } = useQueryJoinedOrganizations();
  const { data: joinedTeamsData } = useQueryJoinedTeams();
  const org = joinedOrgData?.organizations?.at(0);
  const teamId =
    org || isOrgPending ? undefined : joinedTeamsData?.joined_teams.at(0)?.team_id;
  const { data: teamDetails } = useQueryTeamDetails(teamId);

  if (org) {
    return {
      isDirectSharingAllowed: org.allow_direct_session_sharing !== false,
      isLinkSharingAllowed: org.allow_session_link_sharing ?? false,
    };
  }

  const team = isOrgPending ? undefined : teamDetails?.team;

  return {
    isDirectSharingAllowed: team?.allow_direct_session_sharing !== false,
    isLinkSharingAllowed: !isOrgPending && team?.allow_session_link_sharing !== false,
  };
}
